<template>
  <div class="ccma-contracts">
    <h4>{{ t('consultantCorner.contracts.title') }}</h4>
    <p class="section-description">{{ t('consultantCorner.contracts.description') }}</p>

    <div v-if="loading" class="loading-state">
      <p>{{ t('consultantCorner.contracts.loading') }}</p>
    </div>

    <div v-else-if="contracts.length > 0" class="contracts-table">
      <Grid
        :data-items="contracts"
        :resizable="true"
        :pageable="false"
        :columns="columns"
        class="contracts-grid"
      >
        <template v-slot:acknowledgedDateCell="{ props }">
          <td>{{ formatDate(props.dataItem.acknowledgeDate) }}</td>
        </template>
        <template v-slot:signByDateCell="{ props }">
          <td>{{ formatDate(props.dataItem.mustBeSignedByDate) }}</td>
        </template>
      </Grid>
    </div>

    <div v-else class="no-contracts">
      <div class="empty-state">
        <i class="fas fa-file-contract"></i>
        <h5>{{ t('consultantCorner.contracts.noContracts') }}</h5>
        <p>{{ t('consultantCorner.contracts.noContractsDescription') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { Grid } from '@progress/kendo-vue-grid'
import { PwsProfileTermsGetList } from '../../api/ConsultantCorner'
import { formatDate } from '../../api/Common'
import type { IContract } from '../../types/ConsultantCornerTypes'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

const { t } = useI18n()
const logger = useLogger()


const contracts = ref<IContract[]>([])
const loading = ref(false)

const loadContracts = async () => {

  try {
    loading.value = true
    const response = await PwsProfileTermsGetList()
    contracts.value = response.items || []
  } catch (error) {
    logger.error('Error loading contracts:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadContracts()
})

const columns = [
  {
    field: 'versionName',
    title: t('consultantCorner.contracts.name'),
    width: resolveColumnWidth('300px', t('consultantCorner.contracts.name'))
  },
  {
    field: 'versionNumber',
    title: t('consultantCorner.contracts.version'),
    width: resolveColumnWidth('100px', t('consultantCorner.contracts.version'))
  },
  {
    field: 'acknowledgeDate',
    title: t('consultantCorner.contracts.deliveredOn'),
    cell: 'acknowledgedDateCell',
    width: resolveColumnWidth('150px', t('consultantCorner.contracts.deliveredOn'))
  },
  {
    field: 'mustBeSignedByDate',
    title: t('consultantCorner.contracts.signByDate'),
    cell: 'signByDateCell',
    width: resolveColumnWidth('150px', t('consultantCorner.contracts.signByDate'))
  }
]
</script>

<style lang="scss" scoped>
.ccma-contracts {
  padding: $card-padding;
}

.section-description {
  color: #666;
  margin-bottom: 2rem;
  font-style: italic;
}

.contracts-table {
  margin-bottom: 2rem;
  width: 70%;
  margin-left: auto;
  margin-right: auto;
}

.contracts-grid {
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
}

.no-contracts {
  text-align: center;
  padding: 3rem;
}

.empty-state {
  color: #666;
}

.empty-state i {
  @include typography-display;
  color: #e67e22;
  margin-bottom: 1rem;
}

.empty-state h5 {
  margin-bottom: 1rem;
  color: #333;
}

.empty-state p {
  @include typography-body;
  max-width: 400px;
  margin: 0 auto;
}

@media (max-width: 767px) {
  .ccma-contracts {
    padding: $spacing-2-5;
  }

  .contracts-grid {
    @include typography-small;
  }

  .empty-state {
    padding: 2rem 1rem;
  }

  .empty-state i {
    @include typography-h2;
  }
}
</style>
